import type { ListView } from '@app/constants/list-views';
import { useCalendarUiFlag } from '@app/features/calendar/use-calendar-ui-flag';
import {
  CREATABLE_BLOCKS,
  type CreatableBlock,
  runCreateAction,
} from '@app/features/command/Launcher';
import { SearchState } from '@app/features/command/mobile/mobileSearchState';
import { useFeatureFlag } from '@app/lib/analytics/posthog';
import { globalSplitManager } from '@app/signal/splitLayout';
import { useHandleFileUpload } from '@app/util/handleFileUpload';
import {
  ENABLE_ANIMATED_ICONS,
  ENABLE_SNIPPETS_FLAG,
  ENABLE_SNIPPETS_OVERRIDE,
} from '@core/constant/featureFlags';
import { useSettingsState } from '@core/constant/SettingsState';
import { triggerFocusInput } from '@core/directive/focusInput';
import { hapticImpact } from '@core/mobile/haptics';
import { openFilePicker } from '@core/util/upload';
import { ICON_ANIMATION_DURATION_MS } from '@icon/animation';
import IconGear from '@icon/macro-gear.svg';
import WideCalendarIcon from '@icon/wide-calendar.svg';
import { AnimatedCallIcon } from '@icon/wide-call';
import { AnimatedChannelIcon } from '@icon/wide-channel';
import { AnimatedEmailIcon } from '@icon/wide-email';
import { AnimatedFileMdIcon } from '@icon/wide-fileMd';
import { AnimatedFolderIcon } from '@icon/wide-folder';
import { AnimatedInboxIcon } from '@icon/wide-inbox';
import { AnimatedSearchIcon } from '@icon/wide-search';
import { AnimatedStarIcon } from '@icon/wide-star';
import { AnimatedTaskIcon } from '@icon/wide-task';
import CaretUpIcon from '@phosphor/caret-up.svg';
import PlusIcon from '@phosphor/plus.svg';
import UploadIcon from '@phosphor/upload-simple.svg';
import { useLocation } from '@solidjs/router';
import { cn } from '@ui';
import { createSignal, Show } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { useSplitLayout } from '../split-layout/layout';
import {
  type MobileTouchIconComponent,
  MobileTouchMenu,
  type MobileTouchMenuItem,
} from './MobileTouchMenu';
import { pressPulse } from './pressPulse';

// Keeps the directive import from being tree-shaken / lint-flagged.
false && pressPulse;

type DockId = ListView | 'calendar';

type MobileDockButtonProps = {
  icon: MobileTouchIconComponent;
  label?: string;
  /** Accessible name for icon-only buttons (falls back to `label`). */
  ariaLabel?: string;
  onClick: () => void;
  active?: boolean;
  ref?: HTMLButtonElement | ((el: HTMLButtonElement) => void);
  onTouchMove?: (e: TouchEvent) => void;
  onTouchEnd?: (e: TouchEvent) => void;
  iconClass?: string;
  class?: string;
  /** Plain svg icons (e.g. Caret) don't accept `triggerAnimation`. */
  animateIcon?: boolean;
};

function MobileDockButton(props: MobileDockButtonProps) {
  const [animating, setAnimating] = createSignal(false);

  return (
    <button
      type="button"
      ref={props.ref}
      aria-label={props.ariaLabel ?? props.label}
      use:pressPulse
      onPointerDown={() => {
        hapticImpact('light');
        if (props.animateIcon !== false) {
          setAnimating(true);
          setTimeout(() => setAnimating(false), ICON_ANIMATION_DURATION_MS);
        }
      }}
      // Fires on release; the press pulse holds the on-state while touched.
      onClick={() => {
        props.onClick();
      }}
      onTouchMove={props.onTouchMove}
      onTouchEnd={props.onTouchEnd}
      class={cn(
        'island pointer-events-auto flex items-center justify-center',
        props.active && 'text-accent',
        props.class
      )}
    >
      <div class={cn('size-6 [&_svg]:size-6 shrink-0', props.iconClass)}>
        {props.animateIcon === false ? (
          <Dynamic component={props.icon} />
        ) : (
          <Dynamic component={props.icon} triggerAnimation={animating()} />
        )}
      </div>
      <Show when={props.label}>
        <span class="text-sm font-medium truncate">{props.label}</span>
      </Show>
    </button>
  );
}

function MoreViewsMenu(props: {
  isActive: (id: DockId) => boolean;
  onNavigate: (id: DockId) => void;
}) {
  const { settingsOpen, toggleSettings } = useSettingsState();
  const calendarUiEnabled = useCalendarUiFlag();

  // Rows render top → bottom, ending at the thumb, so bottom → top this reads
  // Inbox, Email, Channels, Documents, Tasks, Agents, Calls, Folders — keep
  // that order in sync with the Create menu's matching entries.
  const moreViews = (): Array<{
    id: DockId;
    label: string;
    icon: MobileTouchIconComponent;
  }> => [
    { id: 'folders', label: 'Folders', icon: AnimatedFolderIcon },
    { id: 'calls', label: 'Calls', icon: AnimatedCallIcon },
    { id: 'agents', label: 'Agents', icon: AnimatedStarIcon },
    { id: 'tasks', label: 'Tasks', icon: AnimatedTaskIcon },
    ...(calendarUiEnabled()
      ? [{ id: 'calendar' as const, label: 'Calendar', icon: WideCalendarIcon }]
      : []),
    { id: 'documents', label: 'Documents', icon: AnimatedFileMdIcon },
    { id: 'channels', label: 'Channels', icon: AnimatedChannelIcon },
    { id: 'mail', label: 'Email', icon: AnimatedEmailIcon },
    { id: 'inbox', label: 'Inbox', icon: AnimatedInboxIcon },
  ];

  return (
    <MobileTouchMenu
      triggerIcon={CaretUpIcon}
      footerLabel="Views"
      footerCaretClass="mr-11"
      items={[
        {
          id: 'settings',
          label: 'Settings',
          icon: IconGear,
          active: settingsOpen,
          animateIcon: false,
          onSelect: toggleSettings,
        },
        ...moreViews().map((item) => ({
          id: item.id,
          label: item.label,
          icon: item.icon,
          active: () => props.isActive(item.id),
          onSelect: () => props.onNavigate(item.id),
        })),
      ]}
    />
  );
}

// Rows render top → bottom, ending at the thumb, so bottom → top this reads
// Email, Message, Doc, Task, Agent, Folder, Code, Canvas, Snippet — keep the
// entries both menus share in sync with the Views menu order. Each name
// resolves to its first CREATABLE_BLOCKS match, which drops the
// desktop-only 'Channel' entry: it shares the 'channel' blockName with
// 'Message', so on mobile both would run the same create action.
const CREATE_MENU_BLOCK_ORDER: CreatableBlock['blockName'][] = [
  'snippet',
  'canvas',
  'code',
  'project',
  'chat',
  'task',
  'md',
  'channel',
  'email',
];

function CreateMenu() {
  const snippetsFlag = useFeatureFlag(ENABLE_SNIPPETS_FLAG, {
    enabledOverride: ENABLE_SNIPPETS_OVERRIDE,
  });
  const handleFileUpload = useHandleFileUpload();

  const blocks = () =>
    CREATE_MENU_BLOCK_ORDER.filter(
      (blockName) => blockName !== 'snippet' || snippetsFlag().enabled
    ).flatMap((blockName) => {
      const block = CREATABLE_BLOCKS.find((b) => b.blockName === blockName);
      return block ? [block] : [];
    });

  const uploadItem: MobileTouchMenuItem = {
    id: 'upload-file',
    label: 'Upload file',
    icon: UploadIcon,
    animateIcon: false,
    onSelect: () => {
      openFilePicker({ multiple: true }, async (files) => {
        await handleFileUpload(files, false);
      });
    },
  };

  return (
    <MobileTouchMenu
      triggerIcon={PlusIcon}
      footerLabel="Create"
      items={[
        uploadItem,
        ...blocks().map((block) => {
          const useAnimatedIcon = ENABLE_ANIMATED_ICONS && block.animatedIcon;
          return {
            id: block.blockName,
            label: block.label,
            icon: useAnimatedIcon ? block.animatedIcon : block.icon,
            animateIcon: !!useAnimatedIcon,
            onSelect: () => runCreateAction(block.blockName),
          };
        }),
      ]}
    />
  );
}

type MobileDockProps = {
  class?: string;
};

export function MobileDock(props: MobileDockProps) {
  const { openWithSplit } = useSplitLayout();
  const location = useLocation();

  const isActive = (id: DockId) => {
    const activeContent = globalSplitManager()?.activeSplit()?.content();
    if (!activeContent) {
      const segments = location.pathname.split('/').filter(Boolean);
      return segments[segments.length - 1] === id;
    }
    return activeContent.id === id;
  };

  const navigate = (id: DockId) => {
    // If we're already on a soup/component view, replace in-place (mergeHistory)
    // so the tab switch doesn't push a new entry into the swipe-back BG slot.
    // From any other view (document, task, etc.) treat it as forward navigation
    // so the user can swipe back to where they were.
    const fgContent = globalSplitManager()?.activeSplit()?.content();
    const isOnSoupView = fgContent?.type === 'component';
    openWithSplit({ type: 'component', id }, { mergeHistory: isOnSoupView });
  };

  return (
    <div
      class={cn(
        'flex items-center gap-3 px-(--mobile-chrome-gutter)',
        props.class
      )}
    >
      <MobileDockButton
        icon={AnimatedInboxIcon}
        ariaLabel="Inbox"
        class="size-10 rounded-full shrink-0"
        active={isActive('inbox')}
        onClick={() => navigate('inbox')}
      />
      <MobileDockButton
        icon={AnimatedEmailIcon}
        ariaLabel="Email"
        class="size-10 rounded-full shrink-0"
        active={isActive('mail')}
        onClick={() => navigate('mail')}
      />
      <MobileDockButton
        icon={AnimatedSearchIcon}
        label="Search"
        class="h-10 flex-1 min-w-0 gap-1 rounded-full px-3"
        onClick={() => {
          SearchState.maybeResetState();
          // Arm the focus before opening: iOS only raises the keyboard for a
          // synchronous focus inside the gesture, so triggerFocusInput grabs a
          // temp input now and transfers to the real search input once the
          // dock region portals it in.
          triggerFocusInput(() =>
            document.getElementById('mobile-search-input')
          );
          SearchState.open();
        }}
      />
      <MoreViewsMenu isActive={isActive} onNavigate={navigate} />
      <CreateMenu />
    </div>
  );
}
